/**
 * OtaBadges — Kanalzustand eines Apartments in der Liste.
 *
 * Eine Reihe kleiner Markenzeichen, eines je verbundenem Kanal — bei fünfzig Zeilen schneller
 * scanbar als Text. Ist die Veröffentlichung nicht bereit, steht dort Text statt Symbol: eine
 * Farbe allein erklärt keinen Zustand (§2/§11).
 */
import type { ChannexChannelResource } from '@/shared/api';
import { OtaLogo, getOtaLabel } from './OtaLogo';

interface OtaBadgesProps {
  channexPropertyId: string | null | undefined;
  publicationHealth: string | null | undefined;
  /**
   * Die Kanäle hängen am Objekt und sind für alle Geschwister-Apartments identisch. Ob **dieses**
   * Apartment über sie verkauft wird, sagt allein seine eigene Veröffentlichung — ohne diese
   * Bedingung trüge ein rein lokales Apartment die Logos seiner Geschwister.
   */
  unitPublished: boolean;
  channels: ChannexChannelResource[];
}

const HEALTH_LABEL: Record<string, string> = {
  Disconnected: 'Nicht verbunden',
  Publishing: 'Wird veröffentlicht',
  RetryScheduled: 'Erneuter Versuch geplant',
  Failed: 'Veröffentlichung prüfen',
};

export function OtaBadges({
  channexPropertyId,
  unitPublished,
  publicationHealth,
  channels,
}: OtaBadgesProps) {
  if (publicationHealth && publicationHealth !== 'Ready') {
    // Ein unbekannter Zustand darf keine leere Zelle erzeugen — dann stünde dort nichts, und
    // „nichts" liest sich wie „nicht verbunden".
    return (
      <span className="text-ink-soft text-xs">
        {HEALTH_LABEL[publicationHealth] ?? 'Zustand unbekannt'}
      </span>
    );
  }

  if (!channexPropertyId || !unitPublished) {
    return <span className="text-ink-soft text-xs">Nicht verbunden</span>;
  }

  const matched = channels.filter((c) => c.attributes.properties?.includes(channexPropertyId));
  if (matched.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="bg-ink-faint size-1.5 rounded-full" aria-hidden />
        <span className="text-ink-soft text-xs">Bereit, kein Kanal verbunden</span>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      {matched.map((c) => {
        const otaLabel = getOtaLabel(c.attributes.channel);
        return (
          <OtaLogo
            key={c.id}
            channel={c.attributes.channel}
            active={publicationHealth === 'Ready'}
            size={20}
            title={`${otaLabel} · Bereit`}
          />
        );
      })}
    </div>
  );
}
